import { useState, useRef, useCallback, useEffect } from 'react';
import { getComments, createComment } from '../services/api';
import EmojiPicker from './EmojiPicker';
import ContextualAdSlot from './ContextualAdSlot';
import ClickToPlayVideo from './ClickToPlayVideo';
import { getApiBase } from '../services/runtimeConfig';

const API_BASE = getApiBase();
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'video/mp4', 'video/webm', 'video/quicktime'];
const MAX_FILES = 5;
const MAX_SIZE_MB = 50;

function CommentMedia({ url }) {
  const fullUrl = `${API_BASE}${url}`;
  const isVideo = /\.(mp4|webm|mov)$/i.test(url);
  if (isVideo) {
    return <ClickToPlayVideo src={fullUrl} className="comment-media-item" thumbClassName="comment-video-thumb" />;
  }

  return (
    <img className="comment-media-item" src={fullUrl} alt="attachment" loading="lazy" />
  );
}

export default function CommentSection({ postId, initialCount = 0, selectedCountry = '', forceOpen = false }) {
  const [comments, setComments] = useState([]);
  const [commentsCount, setCommentsCount] = useState(initialCount);
  const [sortBy, setSortBy] = useState('oldest');
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState('');
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [sortOpen, setSortOpen] = useState(false);
  const fileRef = useRef();
  const textRef = useRef();
  const sortRef = useRef(null);

  const loadComments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getComments(postId, sortBy);
      setComments(data);
      setCommentsCount(data.length);
      setLoaded(true);
    } catch {
      setError('Failed to load comments.');
    } finally {
      setLoading(false);
    }
  }, [postId, sortBy]);

  function insertEmoji(emoji) {
    const el = textRef.current;
    if (!el) {
      setContent((prev) => `${prev}${emoji}`);
      return;
    }

    const start = el.selectionStart ?? content.length;
    const end = el.selectionEnd ?? content.length;
    const next = `${content.slice(0, start)}${emoji}${content.slice(end)}`;
    setContent(next);

    requestAnimationFrame(() => {
      el.focus();
      const cursor = start + emoji.length;
      el.setSelectionRange(cursor, cursor);
    });
  }

  function toggleComments() {
    setOpen((prev) => !prev);
  }

  useEffect(() => {
    if (forceOpen) setOpen(true);
  }, [forceOpen]);

  useEffect(() => {
    if (open) {
      loadComments();
    }
  }, [open, sortBy, loadComments]);

  useEffect(() => {
    if (!sortOpen) return;

    function handleOutside(e) {
      if (sortRef.current && !sortRef.current.contains(e.target)) {
        setSortOpen(false);
      }
    }

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [sortOpen]);

  function handleFiles(selected) {
    const valid = [];
    const newPreviews = [];
    for (const file of selected) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        setError(`"${file.name}" is not a supported type.`);
        return;
      }
      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        setError(`"${file.name}" exceeds ${MAX_SIZE_MB} MB.`);
        return;
      }
      valid.push(file);
      newPreviews.push({ url: URL.createObjectURL(file), type: file.type, name: file.name });
    }
    if (files.length + valid.length > MAX_FILES) {
      setError(`Maximum ${MAX_FILES} files per comment.`);
      return;
    }
    setError('');
    setFiles((prev) => [...prev, ...valid]);
    setPreviews((prev) => [...prev, ...newPreviews]);
  }

  function removeFile(idx) {
    URL.revokeObjectURL(previews[idx].url);
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setPreviews((prev) => prev.filter((_, i) => i !== idx));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!content.trim()) return;
    setSubmitting(true);
    setUploadProgress(0);
    setError('');
    try {
      const comment = await createComment(postId, content.trim(), files, selectedCountry, {
        onProgress: (pct) => setUploadProgress(pct),
      });
      setComments((prev) => (sortBy === 'newest' ? [comment, ...prev] : [...prev, comment]));
      setCommentsCount((prev) => prev + 1);
      setContent('');
      previews.forEach((p) => URL.revokeObjectURL(p.url));
      setFiles([]);
      setPreviews([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  }

  return (
    <div className="comment-section">
      <button className="btn-ghost comment-toggle" onClick={toggleComments}>
        {open ? '▲ Hide 💬' : '▼ Comments 💬'} ({commentsCount})
      </button>

      {open && (
        <div className="comment-body">
          <div className="comment-toolbar">
            <div className={`feed-control-sort comment-sort-control${sortOpen ? ' feed-sort-open' : ''}`} ref={sortRef}>
              <button
                type="button"
                className="btn-control feed-sort-btn"
                aria-label="Sort comments"
                aria-expanded={sortOpen}
                onClick={() => setSortOpen((prev) => !prev)}
              >
                <span className="feed-sort-icon">⇅</span>
                <span className="feed-sort-current">{{ newest: 'Newest', oldest: 'Oldest first' }[sortBy]}</span>
              </button>
              {sortOpen && (
                <ul className="feed-sort-dropdown" role="listbox" aria-label="Comment sort options">
                  {[['newest', 'Newest'], ['oldest', 'Oldest first']].map(([val, label]) => (
                    <li key={val}>
                      <button
                        type="button"
                        className={`feed-sort-option${sortBy === val ? ' feed-sort-option-active' : ''}`}
                        role="option"
                        aria-selected={sortBy === val}
                        onClick={() => {
                          setSortBy(val);
                          setSortOpen(false);
                        }}
                      >
                        {label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {loading && <p className="loading-text">Loading comments…</p>}

          {!loading && comments.length === 0 && loaded && (
            <p className="empty-text">No comments yet. Be the first.</p>
          )}

          <div className="comment-list">
            {comments.map((c) => (
              <div key={c._id} className="comment-item">
                <div className="comment-meta">
                  <span className="comment-username">{c.username}</span>
                  <span className="comment-time">
                    {new Date(c.timestamp).toLocaleString()}
                  </span>
                  {c.placeMentioned && (
                    <span className="location-badge" title="Place mentioned in comment">
                      {c.placeMentioned}
                    </span>
                  )}
                </div>
                <p className="comment-content">{c.content}</p>
                {c.media && c.media.length > 0 && (
                  <div className="comment-media-grid">
                    {c.media.map((url, i) => (
                      <CommentMedia key={i} url={url} />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Add comment form */}
          <form className="comment-form" onSubmit={handleSubmit}>
            <textarea
              ref={textRef}
              className="comment-textarea custom-textarea"
              placeholder="Add an anonymous comment…"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              maxLength={2000}
              rows={3}
              disabled={submitting}
            />
            <div className="comment-tools-row">
              <EmojiPicker onSelect={insertEmoji} disabled={submitting} />

              {/* File attach */}
              <div className="comment-attach-row">
                <button
                  type="button"
                  className="btn-ghost btn-sm"
                  onClick={() => fileRef.current.click()}
                  disabled={submitting || files.length >= MAX_FILES}
                >
                  📎 Attach ({files.length}/{MAX_FILES})
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.mp4,.webm,.mov"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => handleFiles(Array.from(e.target.files))}
                />
              </div>
            </div>

            {/* Previews */}
            {previews.length > 0 && (
              <div className="comment-preview-grid">
                {previews.map((p, i) => (
                  <div key={i} className="preview-item">
                    {p.type.startsWith('image/') ? (
                      <img src={p.url} alt={p.name} />
                    ) : (
                      <video src={p.url} controls />
                    )}
                    <button
                      type="button"
                      className="preview-remove"
                      onClick={() => removeFile(i)}
                    >✕</button>
                  </div>
                ))}
              </div>
            )}

            {error && <p className="form-error">{error}</p>}

            {submitting && files.length > 0 && (
              <div className="upload-progress" aria-live="polite">
                <div className="upload-progress-track">
                  <div className="upload-progress-fill" style={{ width: `${uploadProgress}%` }} />
                </div>
                <span className="upload-progress-label">Uploading attachments... {uploadProgress}%</span>
              </div>
            )}

            <button
              type="submit"
              className="btn-primary"
              disabled={submitting || !content.trim()}
            >
              {submitting ? 'Posting…' : 'Comment Anonymously'}
            </button>
          </form>

          <ContextualAdSlot postId={postId} text={comments.map((c) => c.content).join(' ')} adType="banner" />
        </div>
      )}
    </div>
  );
}
